import axios from 'axios';
import type {
  NotificationHistoryDto,
  NotificationHistorySearchCondition,
  ChannelCatalogItem,
  NotificationStatsResponse,
  SendNotificationRequest,
  PageResponse,
} from '../types/notificationCenter';
import { getStoredToken } from '../../login/api/loginApi';
import { attachAuthInterceptors } from '../../_shared/auth/authInterceptor';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE ?? '/api/eraf',
  headers: { 'Content-Type': 'application/json' },
});
attachAuthInterceptors(apiClient);

// 요청 인터셉터 — Bearer 토큰 첨부
apiClient.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// 응답 인터셉터 — ApiResponse<T> 언래핑
apiClient.interceptors.response.use(
  (response) => {
    const d = response.data as Record<string, unknown> | undefined;
    if (d && typeof d === 'object' && 'success' in d) {
      if (d.success === true) {
        return { ...response, data: d['data'] };
      }
      const err = d['error'] as Record<string, unknown> | undefined;
      return Promise.reject({
        code: err?.['code'] ?? `HTTP-${response.status}`,
        message: err?.['message'] ?? 'Unknown error',
        traceId: (err?.['traceId'] as string) ?? response.headers?.['x-trace-id'],
      });
    }
    return response;
  },
  (error) => {
    const errData = error.response?.data?.error as Record<string, unknown> | undefined;
    const code = errData?.['code'] ?? `HTTP-${error.response?.status ?? 0}`;
    const message =
      (errData?.['message'] as string) ??
      (error.response?.data?.message as string) ??
      error.message ??
      'Network error';
    const traceId = (error.response?.headers?.['x-trace-id'] as string) ?? undefined;
    return Promise.reject({ code, message, traceId });
  }
);

function buildSearchParams(params: NotificationHistorySearchCondition): URLSearchParams {
  const sp = new URLSearchParams();
  if (params.channel) sp.set('channel', params.channel);
  if (params.status) sp.set('status', params.status);
  if (params.from) sp.set('from', params.from);
  if (params.to) sp.set('to', params.to);
  if (params.page !== undefined) sp.set('page', String(params.page));
  if (params.size !== undefined) sp.set('size', String(params.size));
  if (params.sort) sp.set('sort', params.sort);
  return sp;
}

export const notificationCenterApi = {
  /** GET /api/eraf/notification-center/history — 발송 이력 목록 (NOTIFICATION_READ) */
  listHistory: (params: NotificationHistorySearchCondition): Promise<PageResponse<NotificationHistoryDto>> => {
    const sp = buildSearchParams(params);
    return apiClient
      .get<unknown, { data: PageResponse<NotificationHistoryDto> }>(`/notification-center/history?${sp.toString()}`)
      .then((res) => res.data);
  },

  /** GET /api/eraf/notification-center/history/{id} — 발송 이력 상세 */
  getHistory: (id: number): Promise<NotificationHistoryDto> =>
    apiClient
      .get<unknown, { data: NotificationHistoryDto }>(`/notification-center/history/${id}`)
      .then((res) => res.data),

  /** POST /api/eraf/notification-center/history/{id}/resend — 재발송 (NOTIFICATION_RESEND) */
  resend: (id: number): Promise<NotificationHistoryDto> =>
    apiClient
      .post<unknown, { data: NotificationHistoryDto }>(`/notification-center/history/${id}/resend`)
      .then((res) => res.data),

  /** POST /api/eraf/notification-center/send — 신규 알림 발송 (NOTIFICATION_SEND) */
  send: (body: SendNotificationRequest): Promise<NotificationHistoryDto> =>
    apiClient
      .post<unknown, { data: NotificationHistoryDto }>('/notification-center/send', body)
      .then((res) => res.data),

  /** GET /api/eraf/notification-center/channels — 채널 카탈로그 (NOTIFICATION_READ) */
  channels: (): Promise<ChannelCatalogItem[]> =>
    apiClient
      .get<unknown, { data: ChannelCatalogItem[] }>('/notification-center/channels')
      .then((res) => res.data),

  /** GET /api/eraf/notification-center/stats — 채널별 통계 (NOTIFICATION_READ) */
  stats: (params?: { from?: string; to?: string }): Promise<NotificationStatsResponse> => {
    const sp = new URLSearchParams();
    if (params?.from) sp.set('from', params.from);
    if (params?.to) sp.set('to', params.to);
    const query = sp.toString();
    return apiClient
      .get<unknown, { data: NotificationStatsResponse }>(
        `/notification-center/stats${query ? `?${query}` : ''}`
      )
      .then((res) => res.data);
  },
};
